import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { agentDefsApi, agentTemplatesApi } from '@/api/agents'
import { ApiError } from '@/api/client'
import { AgentRow } from '@/components/AgentRow'
import { RefreshFailed } from '@/components/RefreshFailed'
import { ROUTES } from '@/constants/routes'
import { adminCopy, settingsCopy } from '@/copy'
import {
  agentQueryKeys,
  useAgentTemplatesQuery,
  useWorkspaceAgentsQuery,
} from '@/hooks/queries'
import { useMutationToast } from '@/hooks/useMutationToast'
import { useToastStore } from '@/store/toast'
import type { AgentDefInfo, AgentTemplate } from '@/types/api'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { Chip } from '@/ui/Chip/Chip'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { Icon } from '@/ui/Icon/Icon'
import { IconButton } from '@/ui/IconButton/IconButton'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { Menu, type MenuEntry } from '@/ui/Menu/Menu'
import { ConfirmDialog } from '@/ui/Modal/Modal'
import { RowList, RowListItem } from '@/ui/RowList/RowList'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { withValidatedReturnTo } from '@/utils/returnTo'
import styles from './AgentsPanel.module.css'

// ── Agents list ────────────────────────────────────────────────────────────────

type LifecycleKind = 'pause' | 'restore' | 'delete'

interface LifecycleAction {
  agentId: string
  kind: LifecycleKind
}

/** What started a row's in-flight call: its own button, or a retry (Retry now or the timer). */
type LifecycleSource = LifecycleKind | 'retry'

function AgentsList({
  wsId,
  canManage,
  returnTo,
}: {
  wsId: string
  canManage: boolean
  returnTo: string
}) {
  const qc = useQueryClient()
  const addToast = useToastStore((state) => state.addToast)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [busyAgentIds, setBusyAgentIds] = useState<
    Map<string, LifecycleSource>
  >(new Map())
  const [pendingLifecycle, setPendingLifecycle] = useState<
    Record<string, LifecycleAction>
  >({})
  const retryTimers = useRef(
    new Map<string, ReturnType<typeof globalThis.setTimeout>>(),
  )

  const agentsQuery = useWorkspaceAgentsQuery(wsId, {
    includeDeleted: canManage,
    staleTime: 30_000,
  })
  const agentDefs = agentsQuery.data ?? []
  const showSkeleton = useSkeletonDelay(agentsQuery.isPending)

  useEffect(
    () => () => {
      retryTimers.current.forEach((timer) => {
        globalThis.clearTimeout(timer)
      })
      retryTimers.current.clear()
    },
    [],
  )

  function clearRetry(agentId: string) {
    const timer = retryTimers.current.get(agentId)
    if (timer) globalThis.clearTimeout(timer)
    retryTimers.current.delete(agentId)
  }

  function updateCachedAgent(next: AgentDefInfo) {
    qc.setQueryData<AgentDefInfo[]>(
      agentQueryKeys.workspace(wsId, true),
      (current = []) => {
        const exists = current.some(
          (definition) => definition.agent_id === next.agent_id,
        )
        return exists
          ? current.map((definition) =>
              definition.agent_id === next.agent_id ? next : definition,
            )
          : [...current, next]
      },
    )
    qc.setQueryData<AgentDefInfo[]>(
      agentQueryKeys.workspace(wsId, false),
      (current = []) => {
        const without = current.filter(
          (definition) => definition.agent_id !== next.agent_id,
        )
        return next.status === 'deleted' ? without : [...without, next]
      },
    )
    qc.setQueryData(agentQueryKeys.detail(wsId, next.agent_id), next)
  }

  function invalidateAgents(agentId: string) {
    void qc.invalidateQueries({
      queryKey: agentQueryKeys.workspace(wsId, true),
    })
    void qc.invalidateQueries({
      queryKey: agentQueryKeys.workspace(wsId, false),
    })
    void qc.invalidateQueries({
      queryKey: agentQueryKeys.detail(wsId, agentId),
    })
  }

  async function runLifecycle(
    action: LifecycleAction,
    source: LifecycleSource = action.kind,
  ) {
    clearRetry(action.agentId)
    setBusyAgentIds((current) => new Map(current).set(action.agentId, source))
    try {
      const response =
        action.kind === 'delete'
          ? await agentDefsApi.deleteWithMeta(wsId, action.agentId)
          : await agentDefsApi.updateWithMeta(wsId, action.agentId, {
              status: action.kind === 'pause' ? 'paused' : 'active',
            })
      updateCachedAgent(response.data)
      invalidateAgents(action.agentId)
      setConfirmDeleteId(null)

      if (response.status === 202) {
        setPendingLifecycle((current) => ({
          ...current,
          [action.agentId]: action,
        }))
        addToast(settingsCopy.agents.updatePending(response.data.name), 'info')
        const delay = Math.max(250, (response.retryAfterSeconds ?? 1) * 1_000)
        const timer = globalThis.setTimeout(() => {
          retryTimers.current.delete(action.agentId)
          void runLifecycle(action, 'retry')
        }, delay)
        retryTimers.current.set(action.agentId, timer)
      } else {
        setPendingLifecycle((current) => {
          const next = { ...current }
          delete next[action.agentId]
          return next
        })
      }
    } catch (error) {
      addToast(
        settingsCopy.agents.lifecycleFailure(
          action.kind,
          error instanceof ApiError ? error.status : undefined,
        ),
        'error',
      )
    } finally {
      setBusyAgentIds((current) => {
        const next = new Map(current)
        next.delete(action.agentId)
        return next
      })
    }
  }

  const confirmDeleteAgent = agentDefs.find(
    (d) => d.agent_id === confirmDeleteId,
  )

  const newHref = withValidatedReturnTo(ROUTES.agentNew(wsId), returnTo, {
    allowedKinds: ['admin'],
  })

  function retryLifecycle(action: LifecycleAction) {
    clearRetry(action.agentId)
    void runLifecycle(action, 'retry')
  }

  const editHref = (def: AgentDefInfo) =>
    withValidatedReturnTo(ROUTES.agentEdit(wsId, def.agent_id), returnTo, {
      allowedKinds: ['admin'],
    })

  function lifecycleLabel(action: LifecycleAction | undefined) {
    if (!action) return null
    return settingsCopy.agents.pending[action.kind]
  }

  const newAgent = (
    <Button variant="ghost" href={newHref} state={{ wsId, returnTo }}>
      <Icon name="plus" size="icon-embedded" />
      {settingsCopy.agents.new}
    </Button>
  )

  return (
    <section>
      <SectionHeader rule trailingSpacing="section">
        {settingsCopy.agents.heading}
      </SectionHeader>

      <Stack gap="space-12">
        {agentsQuery.isError && agentsQuery.data && (
          <RefreshFailed
            message={adminCopy.refreshFailed.agents}
            onRetry={() => void agentsQuery.refetch()}
          />
        )}

        {!wsId ? (
          <EmptyState variant="inline" description={adminCopy.noWorkspaces} />
        ) : agentsQuery.isPending ? (
          <div aria-busy="true">{showSkeleton && <Skeleton lines={3} />}</div>
        ) : agentsQuery.isError && !agentsQuery.data ? (
          <Alert tone="danger">
            <Inline gap="space-8" align="baseline" wrap>
              <Text>{settingsCopy.loadFailed.agents}</Text>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void agentsQuery.refetch()}
              >
                {adminCopy.retry}
              </Button>
            </Inline>
          </Alert>
        ) : agentDefs.length === 0 ? (
          <>
            <EmptyState
              variant="inline"
              description={settingsCopy.agents.empty}
            />
            {canManage && (
              <RowList aria-label={settingsCopy.agents.listLabel}>
                <RowListItem>{newAgent}</RowListItem>
              </RowList>
            )}
          </>
        ) : (
          <RowList aria-label={settingsCopy.agents.listLabel}>
            {agentDefs.map((def) => {
              const pending = pendingLifecycle[def.agent_id]
              const source = busyAgentIds.get(def.agent_id)
              const busy = source !== undefined
              // The row's actions as workspace settings draws them (D132): Edit
              // visible, Pause or Restore and Delete in the menu, Retry now
              // while a change is pending.
              const items: MenuEntry[] = []
              if (def.status === 'active') {
                items.push({
                  id: 'pause',
                  label: settingsCopy.agents.pause,
                  onSelect: () =>
                    void runLifecycle({ agentId: def.agent_id, kind: 'pause' }),
                })
              } else if (def.status === 'paused' || def.status === 'deleted') {
                items.push({
                  id: 'restore',
                  label: settingsCopy.agents.restore,
                  onSelect: () =>
                    void runLifecycle({
                      agentId: def.agent_id,
                      kind: 'restore',
                    }),
                })
              }
              if (def.status !== 'deleted') {
                if (items.length > 0) {
                  items.push({ type: 'separator', id: 'sep' })
                }
                items.push({
                  id: 'delete',
                  label: settingsCopy.agents.delete,
                  tone: 'danger',
                  onSelect: () => setConfirmDeleteId(def.agent_id),
                })
              }
              const actions = (
                <>
                  {pending && (
                    <Button
                      variant="ghost"
                      loading={source === 'retry'}
                      disabled={busy && source !== 'retry'}
                      onClick={() => retryLifecycle(pending)}
                    >
                      {settingsCopy.agents.retryNow}
                    </Button>
                  )}
                  {def.status !== 'deleted' &&
                    (busy || pending ? (
                      <Button variant="ghost" disabled>
                        {settingsCopy.agents.edit}
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        href={editHref(def)}
                        state={{ agent: def, wsId, returnTo }}
                      >
                        {settingsCopy.agents.edit}
                      </Button>
                    ))}
                  {items.length > 0 && (
                    <Menu
                      placement="bottom-end"
                      trigger={
                        <IconButton
                          label={settingsCopy.agentActions(def.name)}
                          disabled={busy || !!pending}
                        >
                          <Icon name="dots-horizontal" />
                        </IconButton>
                      }
                      items={items}
                    />
                  )}
                </>
              )
              return (
                <AgentRow
                  key={def.agent_id}
                  agent={def}
                  pendingLabel={lifecycleLabel(pending)}
                  actions={canManage ? actions : undefined}
                />
              )
            })}
            {canManage && <RowListItem>{newAgent}</RowListItem>}
          </RowList>
        )}
      </Stack>

      <ConfirmDialog
        open={confirmDeleteId !== null}
        title={settingsCopy.agents.deleteDialog.title}
        tone="default"
        description={
          confirmDeleteAgent
            ? settingsCopy.agents.deleteDialog.description(
                confirmDeleteAgent.name,
              )
            : undefined
        }
        confirmLabel={settingsCopy.agents.deleteDialog.confirm}
        onConfirm={() => {
          if (confirmDeleteId) {
            void runLifecycle({ agentId: confirmDeleteId, kind: 'delete' })
          }
        }}
        onClose={() => setConfirmDeleteId(null)}
        loading={confirmDeleteId ? busyAgentIds.has(confirmDeleteId) : false}
      />
    </section>
  )
}

// ── Templates list ─────────────────────────────────────────────────────────────

function TemplateRow({
  t,
  canManage,
  onDelete,
}: {
  t: AgentTemplate
  canManage: boolean
  onDelete: () => void
}) {
  const isBuiltin = t.id.startsWith('builtin:')
  return (
    <RowListItem>
      <Stack gap="space-4" className={styles.grow}>
        <Inline gap="space-8">
          <Text>{t.name}</Text>
          {isBuiltin && (
            <Chip variant="kind" label={adminCopy.templates.builtIn} />
          )}
        </Inline>
        {t.description && (
          <Text variant="caption" tone="text-mute">
            {t.description}
          </Text>
        )}
        <span>
          <Chip variant="kind" label={t.model} />
        </span>
      </Stack>
      {canManage && (
        <Inline gap="space-8" wrap={false}>
          {!isBuiltin && (
            <Button variant="ghost" onClick={onDelete}>
              {adminCopy.templates.delete}
            </Button>
          )}
        </Inline>
      )}
    </RowListItem>
  )
}

function TemplatesList({
  wsId,
  canManage,
}: {
  wsId: string
  canManage: boolean
}) {
  const qc = useQueryClient()
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  const templatesQuery = useAgentTemplatesQuery(wsId)
  const templates = templatesQuery.data ?? []
  const showSkeleton = useSkeletonDelay(templatesQuery.isPending)

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['agent-templates', wsId] })

  const deleteMutation = useMutationToast({
    mutationFn: (id: string) => agentTemplatesApi.delete(wsId, id),
    onSuccess: () => {
      void invalidate()
      setConfirmDeleteId(null)
    },
    errorMessage: adminCopy.templates.deleteFailed,
  })

  const confirmDeleteTpl = templates.find((t) => t.id === confirmDeleteId)

  return (
    <section>
      <SectionHeader rule trailingSpacing="section">
        {adminCopy.templates.heading}
      </SectionHeader>

      <Stack gap="space-12">
        {templatesQuery.isError && templatesQuery.data && (
          <RefreshFailed
            message={adminCopy.refreshFailed.templates}
            onRetry={() => void templatesQuery.refetch()}
          />
        )}

        {!wsId ? (
          <EmptyState variant="inline" description={adminCopy.noWorkspaces} />
        ) : templatesQuery.isPending ? (
          <div aria-busy="true">{showSkeleton && <Skeleton lines={3} />}</div>
        ) : templatesQuery.isError && !templatesQuery.data ? (
          <Alert tone="danger">
            <Inline gap="space-8" align="baseline" wrap>
              <Text>{adminCopy.loadFailed.templates}</Text>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void templatesQuery.refetch()}
              >
                {adminCopy.retry}
              </Button>
            </Inline>
          </Alert>
        ) : templates.length === 0 ? (
          <EmptyState
            variant="inline"
            description={adminCopy.templates.empty}
          />
        ) : (
          <RowList aria-label={adminCopy.templates.listLabel}>
            {templates.map((t) => (
              <TemplateRow
                key={t.id}
                t={t}
                canManage={canManage}
                onDelete={() => setConfirmDeleteId(t.id)}
              />
            ))}
          </RowList>
        )}
      </Stack>

      <ConfirmDialog
        open={confirmDeleteId !== null}
        title={adminCopy.templates.deleteDialog.title}
        tone="danger"
        description={
          confirmDeleteTpl
            ? adminCopy.templates.deleteDialog.description(
                confirmDeleteTpl.name,
              )
            : undefined
        }
        confirmLabel={adminCopy.templates.deleteDialog.confirm}
        onConfirm={() => {
          if (confirmDeleteId) deleteMutation.mutate(confirmDeleteId)
        }}
        onClose={() => setConfirmDeleteId(null)}
        loading={deleteMutation.isPending}
      />
    </section>
  )
}

// ── Public component ───────────────────────────────────────────────────────────

interface Props {
  wsId: string
  canManage: boolean
  returnTo: string
}

export function AgentsPanel({ wsId, canManage, returnTo }: Props) {
  return (
    <Stack gap="space-24">
      <AgentsList
        key={`agents-${wsId}`}
        wsId={wsId}
        canManage={canManage}
        returnTo={returnTo}
      />
      <TemplatesList
        key={`templates-${wsId}`}
        wsId={wsId}
        canManage={canManage}
      />
    </Stack>
  )
}
